/**
 * Basic Dynicty Tutorial — Iteration 1
 *
 * Bahasa Indonesia yang sangat sederhana. 1–2 kalimat per langkah.
 * TIDAK mengajarkan konfigurasi Jubelio atau Midtrans (itu tutorial terpisah).
 *
 * Kontrak step:
 *   id            : string unik, dipakai untuk progress persistence
 *   route         : opsional, path admin yang harus dibuka sebelum step berjalan
 *   target        : opsional, selector `[data-tour="..."]` yang akan di-spotlight
 *   featureKey    : opsional, kalau feature flag OFF step akan di-SKIP otomatis
 *   title         : judul singkat (2–4 kata)
 *   body          : penjelasan 1–2 kalimat, sederhana, contoh pengisian bila perlu
 *   placement     : "top" | "bottom" | "left" | "right" | "auto" (default auto)
 *   informational : kalau true → tampil di tengah layar tanpa spotlight
 */

export const BASIC_DYNICTY_TUTORIAL = {
  id: "basic-dynicty",
  title: "Dasar Dynicty",
  subtitle: "Kenali menu-menu utama untuk mulai berjualan.",
  estimatedMinutes: 5,
  steps: [
    {
      id: "welcome",
      informational: true,
      title: "Selamat datang di Dynicty",
      body:
        "Panduan singkat ini akan membantu Anda mengenal menu-menu penting untuk mulai berjualan. Anda bisa berhenti kapan saja dan melanjutkannya lagi nanti.",
    },
    {
      id: "dashboard",
      route: "/admin",
      target: '[data-tour="dashboard-page"]',
      title: "Dashboard",
      body:
        "Halaman ini menampilkan ringkasan penjualan dan pesanan Anda. Buka setiap pagi untuk tahu kondisi toko.",
    },
    {
      id: "settings-store",
      route: "/admin/pengaturan",
      target: '[data-tour="settings-store-info"]',
      title: "Isi Info Toko",
      body:
        "Isi nama toko, nomor WhatsApp, dan alamat. Contoh nama toko: Toko Roti Bu Ani.",
    },
    {
      id: "settings-bank",
      route: "/admin/pengaturan",
      target: '[data-tour="settings-bank"]',
      title: "Rekening Bank",
      body:
        "Tambahkan rekening tujuan pembayaran manual. Contoh: BCA 1234567890 a.n. Ani Wijaya.",
    },
    {
      id: "settings-qris",
      route: "/admin/pengaturan",
      target: '[data-tour="settings-qris"]',
      title: "QRIS (Opsional)",
      body:
        "Kalau punya gambar QRIS toko, unggah di sini. Pelanggan bisa bayar tinggal scan pakai HP.",
    },
    {
      id: "branding",
      route: "/admin/branding",
      target: '[data-tour="branding-general"]',
      title: "Identitas Toko",
      body:
        "Atur nama tampilan, tagline, dan logo toko Anda. Contoh tagline: Roti Fresh Setiap Hari.",
    },
    {
      id: "branding-colors",
      route: "/admin/branding",
      target: '[data-tour="branding-colors"]',
      title: "Warna & Logo",
      body:
        "Pilih warna utama dan upload logo. Warna akan otomatis dipakai di seluruh halaman toko.",
    },
    {
      id: "categories",
      route: "/admin/kategori",
      target: '[data-tour="category-add-btn"]',
      title: "Buat Kategori",
      body:
        "Kategori mengelompokkan produk. Contoh: Roti Manis, Roti Tawar, Kue Ulang Tahun.",
    },
    {
      id: "products",
      route: "/admin/produk",
      target: '[data-tour="product-add-btn"]',
      title: "Tambah Produk",
      body:
        "Klik tombol ini untuk memasukkan produk baru. Isi nama, harga, stok, dan foto produk.",
    },
    {
      id: "product-variants",
      route: "/admin/produk",
      target: '[data-tour="product-add-btn"]',
      featureKey: "core.product_variants",
      title: "Pilihan Varian",
      body:
        "Kalau satu produk punya beberapa pilihan (contoh: ukuran S, M, L), aktifkan Pilihan Varian saat menambah produk agar stok tiap pilihan bisa dipisah.",
    },
    {
      id: "banner",
      route: "/admin/banner",
      target: '[data-tour="banner-page"]',
      title: "Banner Homepage",
      body:
        "Banner adalah gambar besar di halaman depan toko. Cocok untuk promo atau produk unggulan.",
    },
    {
      id: "shipping",
      route: "/admin/shipping",
      target: '[data-tour="shipping-store-delivery"]',
      title: "Pengiriman Toko",
      body:
        "Aktifkan Kurir Toko kalau Anda mengantar sendiri. Isi biaya kirim, contoh: Rp 10.000 dalam kota.",
    },
    {
      id: "shipping-flat",
      route: "/admin/shipping",
      target: '[data-tour="shipping-flat-rates"]',
      title: "Ongkir Tetap per Area",
      body:
        "Tetapkan biaya kirim tetap untuk tiap wilayah. Contoh: Jabodetabek Rp 15.000, Luar Jawa Rp 40.000.",
    },
    {
      id: "warehouse",
      route: "/admin/shipping",
      target: '[data-tour="shipping-warehouse"]',
      title: "Alamat Gudang",
      body:
        "Isi alamat gudang atau tempat Anda menyiapkan pesanan. Ini akan tampil di label pengiriman.",
    },
    {
      id: "payment-info",
      informational: true,
      title: "Pembayaran",
      body:
        "Pembayaran manual (Transfer Bank / QRIS) sudah otomatis aktif setelah rekening dan QRIS diisi di Pengaturan. Metode pembayaran otomatis (Midtrans) punya panduan tersendiri.",
    },
    {
      id: "orders",
      route: "/admin/pesanan",
      target: '[data-tour="orders-page"]',
      title: "Kelola Pesanan",
      body:
        "Semua pesanan pelanggan muncul di sini. Klik satu pesanan untuk konfirmasi, atur pengiriman, atau ubah status.",
    },
    {
      id: "customers",
      route: "/admin/pelanggan",
      target: '[data-tour="customers-page"]',
      title: "Pelanggan",
      body:
        "Daftar pelanggan yang pernah beli di toko Anda. Klik satu pelanggan untuk lihat riwayat pembeliannya.",
    },
    {
      id: "promo",
      route: "/admin/promo",
      target: '[data-tour="promo-page"]',
      title: "Voucher & Promo",
      body:
        "Buat kode voucher untuk menarik pelanggan. Contoh: kode HEMAT10 potong 10% minimal belanja Rp 50.000.",
    },
    {
      id: "seo",
      route: "/admin/seo",
      target: '[data-tour="seo-meta"]',
      title: "Agar Mudah Dicari di Google",
      body:
        "Isi Judul dan Deskripsi toko supaya calon pelanggan bisa menemukan Anda di Google. Contoh judul: Toko Roti Bu Ani — Fresh Setiap Hari.",
    },
    {
      id: "preview",
      informational: true,
      title: "Lihat Toko Anda",
      body:
        "Buka tab baru dan kunjungi halaman utama toko untuk melihat hasilnya dari sisi pelanggan. Selamat, toko Anda siap dipakai!",
    },
    {
      id: "done",
      informational: true,
      title: "Selesai",
      body:
        "Panduan Dasar sudah selesai. Anda bisa mengulangnya kapan saja dari Pusat Panduan.",
    },
  ],
};

export const TUTORIALS_LIST = [
  {
    id: "basic-dynicty",
    status: "active",
    title: "Dasar Dynicty",
    description: "Panduan pertama untuk mengenal menu-menu utama dan mulai berjualan.",
    estimatedMinutes: 5,
    stepsCount: BASIC_DYNICTY_TUTORIAL.steps.length,
  },
  {
    id: "jubelio-shipment",
    status: "active",
    title: "Panduan Jubelio Shipment",
    description: "Pelajari cara mengatur ongkir, kurir, resi, dan tracking menggunakan Jubelio.",
    estimatedMinutes: 8,
    stepsCount: 0, // di-inject saat runtime dari JUBELIO_TUTORIAL.steps.length
    featureKey: "integration.jubelio",
    guidePagePath: "/admin/tutorials/jubelio",
  },
  {
    id: "midtrans-payment",
    status: "coming_soon",
    title: "Midtrans Payment",
    description: "Mengaktifkan pembayaran otomatis (kartu, e-wallet, virtual account) via Midtrans. Panduan detail segera hadir.",
    estimatedMinutes: 5,
    stepsCount: 0,
  },
];

export const TUTORIAL_MAP = {
  "basic-dynicty": BASIC_DYNICTY_TUTORIAL,
};

// Sync stepsCount + register Jubelio track lazily to avoid circular import.
import { JUBELIO_TUTORIAL } from "./jubelioShipment";
TUTORIAL_MAP["jubelio-shipment"] = JUBELIO_TUTORIAL;
const _jbCard = TUTORIALS_LIST.find((t) => t.id === "jubelio-shipment");
if (_jbCard) _jbCard.stepsCount = JUBELIO_TUTORIAL.steps.length;
